import React, { useCallback, useState } from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { OptimizeResultConfig, ResultTableColumnType, ResultTableHeader } from '../../../../config/optimizeResultConfig';
import type { OptimiseResultItem, OptimiseResultLevelDetail, OptimiseResultRange } from '../../../../types/optimiseResult';

interface Props {
    results: OptimiseResultItem[];
    config: OptimizeResultConfig;
    isTopLevel: boolean;
}

function formatNum(value: number): string {
    if (Number.isInteger(value)) return String(value);
    return parseFloat(value.toFixed(2)).toString();
}

function formatRange(range: OptimiseResultRange | null): string {
    if (!range) return '-';
    if (range.min === range.max) return formatNum(range.min);
    return `${formatNum(range.min)} - ${formatNum(range.max)}`;
}


const VARIANCE_COLOR: Record<'positive' | 'negative', string> = {
    positive: '#52c41a',
    negative: '#ff4d4f',
};

const TYPE_FLEX: Record<ResultTableColumnType, number> = {
    label:    2,
    range:    1.5,
    string:   1,
    variance: 1,
};

interface TableRowData {
    key: string;
    kpi: string;
    /** Formatted string values for every 'range' column, keyed by col.key. */
    values: Record<string, string>;
    variance_display: string;
    variance_type: 'positive' | 'negative';
    levelDetails: OptimiseResultLevelDetail[];
}

// Parent row always shows level_details[0] values directly.
// Whether the row is expandable depends solely on level_details.length (see rendering).
// Receiving headers here makes the range field extraction fully config-driven — adding a new
// range column to the config requires no changes to this function.
function buildTableData(results: OptimiseResultItem[], headers: ResultTableHeader[]): TableRowData[] {
    const rangeHeaders  = headers.filter((h) => h.type === 'range');
    const stringHeaders = headers.filter((h) => h.type === 'string');
    return results.map((item) => {
        const direct = item.level_details[0] ?? null;
        const values: Record<string, string> = {};
        for (const h of rangeHeaders) {
            const raw = (direct as unknown as Record<string, OptimiseResultRange | null>)?.[h.key] ?? null;
            values[h.key] = formatRange(raw);
        }
        for (const h of stringHeaders) {
            values[h.key] = (direct as unknown as Record<string, string>)?.[h.key] ?? '-';
        }
        return {
            key: item.constraint_name,
            kpi: item.label,
            values,
            variance_display: direct?.variance_display ?? '-',
            variance_type: direct?.variance_type ?? 'positive',
            levelDetails: item.level_details,
        };
    });
}

function renderParentCell(col: ResultTableHeader, row: TableRowData, isExpandable: boolean): React.ReactNode {
    switch (col.type) {
        case 'label':
            return row.kpi;
        case 'range':
        case 'string':
            return isExpandable ? null : (row.values[col.key] ?? '-');
        case 'variance':
            return isExpandable ? null : (
                <span style={{ color: VARIANCE_COLOR[row.variance_type] }} className="font-medium">
                    {row.variance_display}
                </span>
            );
    }
}

function renderChildCell(col: ResultTableHeader, detail: OptimiseResultLevelDetail): React.ReactNode {
    switch (col.type) {
        case 'label':
            return detail.attribute_name;
        case 'range':
            return formatRange((detail as unknown as Record<string, OptimiseResultRange | null>)[col.key] ?? null);
        case 'string':
            return (detail as unknown as Record<string, string>)[col.key] ?? '-';
        case 'variance':
            return (
                <span style={{ color: VARIANCE_COLOR[detail.variance_type] }} className="font-medium">
                    {detail.variance_display}
                </span>
            );
    }
}

const OptimiseResultTable: React.FC<Props> = ({ results, config, isTopLevel }) => {
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const toggleExpand = useCallback((key: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const tableData = buildTableData(results, config.resultTableHeaders);

    return (
        <div className="flex flex-col">
            <div className="flex bg-[#fafafa] border border-[#f0f0f0] rounded-t-md sticky top-0 z-10">
                {config.resultTableHeaders.map((col) => (
                    <div
                        key={col.key}
                        style={{ flex: TYPE_FLEX[col.type] }}
                        className="px-4 py-3 text-sm font-semibold text-[#18181b]"
                    >
                        {col.label}
                    </div>
                ))}
                <div className="w-10 shrink-0" />
            </div>

            <div className="border border-t-0 border-[#f0f0f0] rounded-b-md">
                {tableData.map((row, rowIndex) => {
                    const isExpanded = expandedRows.has(row.key);
                    const nestedCount = row.levelDetails.length;
                    // size === 1 + topLevel → flat, no expand.
                    // size > 1 → always expandable regardless of level.
                    const isExpandable = nestedCount > 1 || (!isTopLevel && nestedCount === 1);

                    return (
                        <div key={row.key}>
                            <div
                                className={`flex items-center border-b border-[#f0f0f0] last:border-none bg-white hover:bg-[#fafafa] transition-colors ${rowIndex === tableData.length - 1 && !isExpanded ? 'last:border-none' : ''}`}
                            >
                                {config.resultTableHeaders.map((col) => (
                                    <div
                                        key={col.key}
                                        style={{ flex: TYPE_FLEX[col.type] }}
                                        className="px-4 py-3.5 text-sm text-[#18181b]"
                                    >
                                        {renderParentCell(col, row, isExpandable)}
                                    </div>
                                ))}
                                <div className="w-10 shrink-0 flex items-center justify-center">
                                    {isExpandable && (
                                        <button
                                            onClick={() => toggleExpand(row.key)}
                                            className="p-1 text-[#595959] hover:text-[#44338a] transition-colors"
                                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                        >
                                            {isExpanded ? <UpOutlined /> : <DownOutlined />}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {isExpandable && isExpanded &&
                                row.levelDetails.map((detail) => (
                                    <div
                                        key={`${row.key}-${detail.attribute_name}`}
                                        className="flex items-center border-b border-[#f0f0f0] last:border-none bg-[#fafafa]"
                                    >
                                        {config.resultTableHeaders.map((col) => (
                                            <div
                                                key={col.key}
                                                style={{ flex: TYPE_FLEX[col.type] }}
                                                className="px-4 py-3 text-sm text-[#595959]"
                                            >
                                                {renderChildCell(col, detail)}
                                            </div>
                                        ))}
                                        <div className="w-10 shrink-0" />
                                    </div>
                                ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

OptimiseResultTable.displayName = 'OptimiseResultTable';
export default React.memo(OptimiseResultTable);
